import {
  Text,
  Spinner,
  Badge,
  Button,
  GreyGrey30,
  GreyGrey80,
  GreyGrey90,
  IconPlus,
  IndigoIndigo60,
  Link,
  Primary13Regular,
  Primary13SemiBold,
  Primary14SemiBold,
  ShadowXs,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Center,
  Box,
  Flex
} from '@sequencehq/core-components'
import {
  formatScheduleEndDate,
  toBillingScheduleStatusBadgeProps
} from '@sequencehq/utils'
import { useNavigate } from 'react-router-dom'
import { useFetchBillingSchedules } from 'Customer/hooks/useFetchBillingSchedules.ts'
import { useCustomerContext } from 'Customer/hooks/useCustomerContext'
import { format } from '@sequencehq/utils/dates'
import { ProtectedComponent } from 'lib/rbac'

export const BillingScheduleTable = () => {
  const navigate = useNavigate()
  const { customer } = useCustomerContext()
  const { billingSchedules, isLoading } = useFetchBillingSchedules({
    customerId: customer.id
  })

  if (isLoading) {
    return (
      <Center height="200px" width="100%" mt={4}>
        <Spinner />
      </Center>
    )
  }

  const isArchived = Boolean(customer.archivedAt)

  return (
    <Box p={4}>
      <Flex justifyContent="space-between" alignItems="center">
        <Text {...Primary14SemiBold}>Schedules</Text>
        <ProtectedComponent permission="org.billing:create">
          <Button
            {...Primary13SemiBold}
            variant="ghost"
            leadingIcon={
              <IconPlus color={isArchived ? undefined : IndigoIndigo60} />
            }
            {...(!isArchived && {
              as: Link.Unstyled,
              to: `/billing-schedules/new?customerId=${customer.id}`
            })}
            disabled={isArchived}
            data-testid="customer.createSchedule"
          >
            Create schedule
          </Button>
        </ProtectedComponent>
      </Flex>
      <TableContainer
        borderRadius="lg"
        overflow="hidden"
        border={`1px solid ${GreyGrey30}`}
        mt={2}
        width="100%"
        boxShadow={ShadowXs}
      >
        <Table variant="v2" width="100%">
          <Thead>
            <Tr>
              <Th width={120}>Schedule</Th>
              <Th>Products</Th>
              <Th width={128}>Status</Th>
              <Th width={120}>Start Date</Th>
              <Th width={120}>End Date</Th>
            </Tr>
          </Thead>
          <Tbody>
            {billingSchedules.length > 0 ? (
              billingSchedules.map(schedule => {
                return (
                  <Tr
                    key={schedule.id}
                    onClick={() =>
                      navigate(
                        schedule.status === 'DRAFT'
                          ? `/billing-schedules/${schedule.id}/edit`
                          : `/billing-schedules/${schedule.id}`
                      )
                    }
                  >
                    <Td>
                      <Text
                        {...Primary13Regular}
                        as={Link.Unstyled}
                        to={
                          schedule.status === 'DRAFT'
                            ? `/billing-schedules/${schedule.id}/edit`
                            : `/billing-schedules/${schedule.id}`
                        }
                        color={IndigoIndigo60}
                        onClick={e => e.stopPropagation()}
                      >
                        View schedule
                      </Text>
                    </Td>
                    <Td isTruncated>
                      {schedule.prices.map(price => price.name).join(', ')}
                    </Td>
                    <Td>
                      <Badge
                        {...toBillingScheduleStatusBadgeProps({
                          status: schedule.status
                        })}
                      />
                    </Td>
                    <Td>{format(schedule.startDate, 'd MMM yyyy')}</Td>
                    <Td>{formatScheduleEndDate(schedule.endDate)}</Td>
                  </Tr>
                )
              })
            ) : (
              <Tr
                sx={{
                  cursor: 'auto!important',
                  _hover: {
                    bgColor: 'inherit!important'
                  }
                }}
              >
                <Td colSpan={5}>
                  <EmptyContent />
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  )
}

const EmptyContent = () => {
  return (
    <Box width="400px" margin="12px auto" textAlign="center">
      <Text {...Primary13SemiBold} color={GreyGrey90}>
        No billing schedules to display
      </Text>
      <Text {...Primary13Regular} color={GreyGrey80}>
        You have not created a billing schedule for this customer yet.
      </Text>
    </Box>
  )
}
